import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { LocalStorageService } from '@sondermind/local-storage';

import { IFeatureFlagService } from '../models/feature-flag.service.interface';
import { FEATURE_FLAG_CI_VALUES } from '../models/feature-flag-ci-values';
import { EvaluationContext, FeatureFlagVariant } from '../models/feature-flag-types';
import { FeatureFlagKey } from '../models/feature-flag-key.enum';
import { IEvaluationOptions } from '../models/evaluation-options.interface';

// E2E tests use this key to store feature flag overrides in localStorage
const AMPLITUDE_FEATURE_FLAG_OVERRIDE_KEY = 'e2e_amplitude_ff_overrides';

/**
 * CI implementation of the feature flag service.
 * This service uses hardcoded values for feature flags during e2e testing and CI builds.
 * It's designed to provide consistent, predictable behavior for automated tests.
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagCiService implements IFeatureFlagService {
  constructor(private localStorageService: LocalStorageService) {}

  anonymousId$(): Observable<string> {
    return of('b187833a-0d1d-4f3b-a969-1c3794c00ad6');
  }

  initialize$(): Observable<boolean> {
    return of(true);
  }

  evaluate$<TPayload = unknown>(
    flagKey: FeatureFlagKey,
    defaultValue: TPayload,
    options?: IEvaluationOptions
  ): Observable<FeatureFlagVariant<TPayload> | null> {
    const value = this.getFlagValue(flagKey, defaultValue);
    const isTPayloadString = typeof value === 'string';

    // If the payload is a string, use the value as the key
    const key = isTPayloadString ? value : flagKey;
    return of({ key, payload: value } as FeatureFlagVariant<TPayload>);
  }

  isEnabled$(flagKey: FeatureFlagKey): Observable<boolean> {
    const value = this.getFlagValue(flagKey, false);
    // Ensure we always return a boolean
    const booleanValue = typeof value === 'boolean' ? value : false;
    return of(booleanValue);
  }

  getVariant$<TPayload = unknown>(
    context: EvaluationContext,
    flagKey: string
  ): Observable<FeatureFlagVariant<TPayload> | null> {
    const value = this.getFlagValue(flagKey as FeatureFlagKey, null);
    if (value === null || value === undefined) {
      return of(null);
    }

    return of({ key: flagKey, payload: value } as FeatureFlagVariant<TPayload>);
  }

  trackExposure$(flagKey: FeatureFlagKey, variant: FeatureFlagVariant): Observable<void> {
    // No-op implementation for CI/testing - we don't need to track exposures
    return of(undefined);
  }

  /**
   * Gets the value for a feature flag, checking e2e overrides first, then CI defaults
   */
  private getFlagValue<T>(flagKey: FeatureFlagKey, defaultValue: T): T {
    // Check for e2e overrides first
    const e2eOverrides = this.getE2EOverrides();
    if (e2eOverrides && e2eOverrides[flagKey] !== undefined) {
      return e2eOverrides[flagKey] as T;
    }

    // Fall back to CI defaults
    const ciFlagValue = FEATURE_FLAG_CI_VALUES[flagKey];
    return ciFlagValue !== undefined ? (ciFlagValue as T) : defaultValue;
  }

  /**
   * Retrieves e2e feature flag overrides from localStorage
   */
  private getE2EOverrides(): Record<string, unknown> | null {
    // Use LocalStorageService to safely get the stored overrides
    const storedOverrides = this.localStorageService.getItem(AMPLITUDE_FEATURE_FLAG_OVERRIDE_KEY);
    if (storedOverrides) {
      try {
        return JSON.parse(storedOverrides);
      } catch (error) {
        // If JSON parsing fails, return null
        return null;
      }
    }

    return null;
  }
}
